import { useEffect, useState } from 'react';
import { IMAGE_ORIENTATION_CACHE } from '../utils/variables';

export const ORIENTATION = {
  LANDSCAPE: 'LANDSCAPE',
  PORTRAIT: 'PORTRAIT',
  UNKNOWN: 'UNKNOWN',
};

export default function useImageOrientation(url) {
  const [data, setData] = useState({});

  useEffect(() => {
    // Clear first, ALWAYS. This hook is keyed on `url`, so it must never report another
    // url's dimensions — but `data` is component state that outlives a url change, and
    // without this reset the previous image's numbers stay readable until the new one
    // loads. Callers whose url changes over the component's life read that as truth:
    // PortraitsGallery's enlarged view sizes its frame from width/height, so paging from a
    // landscape pose to an uncached portrait one opened the portrait in a landscape frame
    // until it loaded; MultiPose Builder reads `orientation` off the current sheet's pose
    // the same way. Resetting hands them `undefined`, which every caller already treats as
    // "not known yet" (the documented render-portrait-then-reflow default).
    //
    // Costs nothing in the common case: the cache below is warm — useImageOrientations
    // prefetches a whole session's poses — so a cached url reads through in the same
    // effect and no intermediate empty state is ever painted.
    setData({});

    if (!url) return undefined;

    if (IMAGE_ORIENTATION_CACHE[url]) {
      setData(IMAGE_ORIENTATION_CACHE[url]);
      return undefined;
    }

    // A probe already in flight for the PREVIOUS url still resolves after we've moved on,
    // and its setData would land on top of the new url's state — re-introducing exactly the
    // stale dimensions the reset above just cleared. The cache write is left unguarded on
    // purpose: it's keyed by that url and is correct whenever it arrives.
    let cancelled = false;

    const img = new Image();

    img.onload = () => {
      const { height, width } = img;

      // >= : squares count as portrait — keep in sync with useImageOrientations.
      const orientation = height >= width ? ORIENTATION.PORTRAIT : ORIENTATION.LANDSCAPE;

      IMAGE_ORIENTATION_CACHE[url] = { height, orientation, width };
      if (!cancelled) setData({ height, orientation, width });
    };

    img.src = url;

    return () => { cancelled = true; };
  }, [url]);

  return data;
}
